import { FunctionComponent, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { ImageButtonGrid } from './image-button-grid.component';
import {
  EngagePostMessageActions,
  SendMessage,
  useIframeEngageActionListener,
  ImagesService,
  capitalizeString,
} from '../services';

interface AttachFileDialogProps {
  accessToken: string;
  api1Origin: string;
  engageOrigin: string;
}

export const AttachFileDialog: FunctionComponent<AttachFileDialogProps> = ({
  accessToken,
  api1Origin,
  engageOrigin,
}) => {
  const eventBody = useIframeEngageActionListener({
    engageOrigin,
    action: EngagePostMessageActions.OPEN_ATTACH_FILE_DIALOG,
  });
  const [availableImages, setAvailableImages] = useState([]);
  const [associationId, setAssociationId] = useState('');
  const [associationType, setAssociationType] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [waitingForImages, setWaitingForImages] = useState(true);

  useEffect(() => {
    setOpen(eventBody != null);

    if (eventBody) {
      setAssociationId(eventBody.associationId);
      setAssociationType(eventBody.associationType);
    }
  }, [eventBody]);

  useEffect(() => {
    const setAvailableImagesByAssociationId = async () => {
      const imagesService = new ImagesService(
        engageOrigin,
        api1Origin,
        accessToken
      );
      const response = await imagesService.getImagesByAssociationId(
        associationId,
        associationType
      );
      const signedUrls = await imagesService.fetchSignedImageUrls(response);

      setAvailableImages(signedUrls);
      setWaitingForImages(false);
    };

    if (open && waitingForImages) {
      setAvailableImagesByAssociationId();
    }
  }, [
    associationId,
    associationType,
    waitingForImages,
    open,
    accessToken,
    engageOrigin,
    api1Origin,
  ]);

  const handleAddPhotos = () => {
    const payload = {
      REQUEST_ACTION: {
        media: selectedImages,
        type: 'ATTACH_MEDIA_TO_MESSAGE',
      },
    };
    SendMessage(payload, engageOrigin, true);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setAssociationId('');
    setAssociationType('');
    setAvailableImages([]);
    setSelectedImages([]);
    setWaitingForImages(true);
  };

  const onToggleSelect = (image) => {
    const index = selectedImages.findIndex((e) => e.jnid === image.jnid);
    if (index > -1) {
      const tempSelectedImages = selectedImages.slice();
      tempSelectedImages.splice(index, 1);
      setSelectedImages(tempSelectedImages);
    } else {
      setSelectedImages([...selectedImages, image]);
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`Upload Photo from ${capitalizeString(associationType)}`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Acceptable image file formats: .jpg, .png, .img and .jpeg. PDF files
            are not supported.
          </DialogContentText>
          <ImageButtonGrid
            availableImages={availableImages}
            selectedImages={selectedImages}
            onToggleSelect={onToggleSelect}
            waitingForImages={waitingForImages}
          ></ImageButtonGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-e2e="add-photos-cancel">
            Cancel
          </Button>
          <Button
            autoFocus
            data-e2e="add-photos-confirm"
            disabled={!availableImages.length}
            onClick={handleAddPhotos}
          >
            Add Photo(s)
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
